/** Fixed-order guidance; no random paragraph rotation, prices or deadline claims. */
import type { LgzService, LgzCountry } from "@/data/lgz";
import type { LgzLocale } from "@/data/lgz/locale-copy";
import { LGZ_LOCALE_GUIDANCE } from "@/data/lgz/locale-editorial";
import { LGZ_LOCALE_UI } from "@/data/lgz/locale-ui";
import { LGZ_COUNTRY_EDITORIAL } from "@/data/lgz/country-editorial";

export interface MatrixSection { heading: string; html: string }
export interface MatrixContent {
  h1: string; lead: string; sections: MatrixSection[];
  faqs: Array<{ q: string; a: string }>;
  steps: Array<{ tag: string; text: string }>; wordCount: number;
}
function escapeHtml(value: string) {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}
function assemble(locale: LgzLocale, subject: string, guidance: { documents: string; action: string; boundary: string }, country?: LgzCountry, inbound = false): MatrixContent {
  const u = LGZ_LOCALE_UI[locale];
  const route = inbound ? `${guidance.action} ${guidance.boundary}` : [u.route, country?.slug === "schengen" ? u.region : country && !country.hague ? u.nonMember : u.future].join(" ");
  const h1 = `${subject} — ${u.suffix}`;
  const lead = `${subject}: ${guidance.action} ${u.lead}`;
  const sections = [
    [u.docsTitle, guidance.documents], [u.actionTitle, guidance.action],
    [u.routeTitle, route], [u.checkTitle, u.check],
    [u.feesTitle, u.fees], [u.boundaryTitle, guidance.boundary],
  ].map(([heading, body]) => ({ heading, html: `<p>${escapeHtml(body)}</p>` }));
  const faqs = [{ q: u.acceptQ, a: `${guidance.boundary} ${u.acceptA}` }, { q: u.presenceQ, a: u.presenceA }];
   const steps = [u.prepare, country ? u.verify : guidance.action, u.submit, u.return].map((text, i) => ({ tag: String(i + 1), text }));
  const wordCount = [h1, lead, ...sections.map(s => s.html), ...faqs.map(f => f.a)].join(" ").split(/\s+/).length;
  return { h1, lead, sections, faqs, steps, wordCount };
}
export function buildLgzLocaleServiceContent(locale: LgzLocale, service: LgzService): MatrixContent {
  const guidance = LGZ_LOCALE_GUIDANCE[locale][service.slug];
  if (!guidance) throw new Error(`Missing LGZ editorial guidance: ${locale}/${service.slug}`);
  return assemble(locale, service.nameEn, guidance, undefined, service.slug === "apostille-inbound");
}
export function buildLgzLocaleCountryContent(locale: LgzLocale, country: LgzCountry): MatrixContent {
  const u = LGZ_LOCALE_UI[locale];
  return assemble(locale, `${u.countryPrefix} ${localizedCountryName(locale, country)}`, {
    documents: u.documents, action: LGZ_COUNTRY_EDITORIAL[country.slug]?.guidance[locale] ?? u.countryAction,
    boundary: country.slug === "schengen" ? `${u.region} ${u.boundary}` : u.boundary,
  }, country);
}
export function lgzLocaleLabels(locale: LgzLocale) { return LGZ_LOCALE_UI[locale]; }

const LGZ_REGION_CODES: Record<string, string> = { usa: "US", uk: "GB", australia: "AU", "new-zealand": "NZ", canada: "CA", japan: "JP", korea: "KR", germany: "DE", france: "FR", italy: "IT", spain: "ES", netherlands: "NL", belgium: "BE", switzerland: "CH", sweden: "SE", norway: "NO", denmark: "DK", finland: "FI", ireland: "IE", portugal: "PT", china: "CN", taiwan: "TW", singapore: "SG", malaysia: "MY", vietnam: "VN", indonesia: "ID", philippines: "PH", uae: "AE", saudi: "SA" };
/** Native-language destination name so non-English pages do not leak English country names. */
export function localizedCountryName(locale: LgzLocale, country: LgzCountry): string {
  const code = LGZ_REGION_CODES[country.slug];
  if (!code || locale === "en") return country.nameEn;
  try { return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? country.nameEn; } catch { return country.nameEn; }
}
